import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'es-toolkit/compat';

import { getFeaturesKeysObjectsWebWorker } from 'utils/features/getFeaturesKeysObjectsWebWorker';
import { useFormatBracketsOptions } from 'utils/formatBrackets/formatBracketsOptions';
import { useFeatureFormatters, useUiSettings } from 'utils/queryHooks/uiConstantsHooks';

export const useParsedBrackets = ({
  bracketsToParse,
  enabled = true,
  stainIndexFilter,
  addStainToFeatureName,
  studyId,
  orchestrationId,
}: {
  bracketsToParse: string[];
  enabled?: boolean;
  stainIndexFilter?: string;
  addStainToFeatureName: boolean;
  studyId: string;
  orchestrationId: string;
}) => {
  const { uiSettings, isLoadingUiSettings } = useUiSettings();
  const { featureFormatters, isLoadingFeatureFormatters } = useFeatureFormatters();

  const formatBracketsOptions = useFormatBracketsOptions({
    addStain: addStainToFeatureName,
    studyId,
  });

  const parseFeatureData = async () =>
    getFeaturesKeysObjectsWebWorker(bracketsToParse, { ...formatBracketsOptions, orchestrationId }, stainIndexFilter);

  const isLoadingFormatterData = isLoadingFeatureFormatters || isLoadingUiSettings || formatBracketsOptions.isLoading;

  const canParseBrackets = !isEmpty(bracketsToParse) && enabled && !isLoadingFormatterData;

  const queryResponse = useQuery({
    queryKey: [
      'featuresData',
      bracketsToParse,
      uiSettings,
      featureFormatters,
      formatBracketsOptions,
      stainIndexFilter,
      addStainToFeatureName,
      studyId,
      orchestrationId,
    ],
    queryFn: parseFeatureData,
    enabled: canParseBrackets,
    staleTime: Infinity, // getFeaturesKeysObj is a pure function, so we can cache it indefinitely
    retry: false,
  });

  return {
    ...queryResponse,
    canParseBrackets,
    isLoading: canParseBrackets && queryResponse.isLoading,
  };
};
